export const DATA_EXTRACTION_PROPERTY_TYPES = [
  'string',
  'number',
  'integer',
  'boolean',
  'array',
  'object',
  'null',
] as const;

const PROPERTY_TYPE_ALIASES: Record<string, DataExtractionPropertyFormValue['dataType']> = {
  string: 'string',
  number: 'number',
  integer: 'integer',
  boolean: 'boolean',
  array: 'array',
  object: 'object',
  null: 'null',
};

type JsonTemplateDocument = {
  properties?: JsonTemplateProperty[];
};

type JsonEditorParseResult = {
  error?: string;
  isTemplate: boolean;
  schema?: ReturnType<typeof buildDataExtractionJsonSchema>;
  properties: DataExtractionPropertyFormValue[];
};

type JsonTemplateProperty = {
  identifier?: string;
  dataType?: string;
  description?: string;
  required?: boolean;
  enumValues?: string[];
  arrayItemType?: string | null;
};

export const DATA_EXTRACTION_ENUM_TYPES = ['string', 'number', 'integer', 'boolean'] as const;
export const DATA_EXTRACTION_SCHEMA_MODES = ['builder', 'json'] as const;

export type DataExtractionPropertyType = (typeof DATA_EXTRACTION_PROPERTY_TYPES)[number];
export type DataExtractionEnumType = (typeof DATA_EXTRACTION_ENUM_TYPES)[number];
export type DataExtractionArrayItemType = Exclude<DataExtractionPropertyType, 'array'>;
export type DataExtractionSchemaMode = (typeof DATA_EXTRACTION_SCHEMA_MODES)[number];

export type JsonSchemaEnumValue = string | number | boolean | null;

export type JsonSchema = {
  type?: DataExtractionPropertyType | DataExtractionPropertyType[];
  description?: string;
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema;
  enum?: JsonSchemaEnumValue[];
  required?: string[];
  additionalProperties?: boolean | JsonSchema;
};

export interface DataExtractionPropertyFormValue {
  id: string;
  identifier: string;
  description: string;
  dataType: DataExtractionPropertyType;
  required: boolean;
  arrayItemType: DataExtractionArrayItemType | null;
  enumValues: string[];
}

const DEFAULT_ARRAY_ITEM_TYPE: DataExtractionArrayItemType = 'string';

type BuildDataExtractionJsonSchemaOptions = {
  rootIsArray?: boolean;
};

const createPropertyId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `property-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const isPropertyType = (value: unknown): value is DataExtractionPropertyType => {
  return (
    typeof value === 'string' &&
    DATA_EXTRACTION_PROPERTY_TYPES.includes(value as DataExtractionPropertyType)
  );
};

const pickSchemaType = (schema?: JsonSchema): DataExtractionPropertyType => {
  const rawType = Array.isArray(schema?.type) ? schema.type[0] : schema?.type;

  if (isPropertyType(rawType)) {
    return rawType;
  }

  if (schema?.items) {
    return 'array';
  }

  if (schema?.properties || schema?.additionalProperties) {
    return 'object';
  }

  return 'string';
};

const normalizeEnumValues = (values: string[]) => values.map(value => value.trim()).filter(Boolean);

const parseEnumValue = (value: string, type: DataExtractionEnumType): JsonSchemaEnumValue => {
  switch (type) {
    case 'number':
      return Number(value);
    case 'integer':
      return parseInt(value, 10);
    case 'boolean':
      return value.toLowerCase() === 'true';
    default:
      return value;
  }
};

const stringifyEnumValue = (value: JsonSchemaEnumValue) => {
  if (value === null) {
    return 'null';
  }

  return String(value);
};

const isJsonSchemaObject = (value: unknown): value is JsonSchema => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isScalarSchemaCompatible = (schema?: JsonSchema) => {
  const type = pickSchemaType(schema);

  if (type === 'array' || type === 'object') {
    return false;
  }

  if (schema?.items || schema?.properties) {
    return false;
  }

  return true;
};

const isPropertySchemaBuilderCompatible = (schema?: JsonSchema) => {
  if (!schema || Array.isArray(schema.type)) {
    return false;
  }

  const type = pickSchemaType(schema);

  if (type === 'array') {
    return isScalarSchemaCompatible(schema.items);
  }

  if (type === 'object') {
    return !schema.properties && !schema.items && (schema.additionalProperties ?? true) === true;
  }

  return !schema.properties && !schema.items;
};

const isDataExtractionObjectRootBuilderCompatible = (schema?: JsonSchema | null) => {
  if (!schema || Array.isArray(schema.type)) {
    return false;
  }

  const rootType = schema.type;

  if (rootType !== undefined && rootType !== 'object') {
    return false;
  }

  if (schema.items || schema.enum) {
    return false;
  }

  if (schema.additionalProperties !== undefined && schema.additionalProperties !== false) {
    return false;
  }

  if (!schema.properties) {
    return true;
  }

  return Object.values(schema.properties).every(propertySchema =>
    isPropertySchemaBuilderCompatible(propertySchema)
  );
};

export const isDataExtractionRootArraySchema = (schema?: JsonSchema | null) => {
  if (!schema || Array.isArray(schema.type)) {
    return false;
  }

  const rootType = pickSchemaType(schema);

  if (rootType !== 'array') {
    return false;
  }

  if (schema.enum || schema.properties || schema.additionalProperties !== undefined) {
    return false;
  }

  return isDataExtractionObjectRootBuilderCompatible(schema.items);
};

const getDataExtractionObjectRootSchema = (schema?: JsonSchema | null) => {
  if (!schema) {
    return undefined;
  }

  return isDataExtractionRootArraySchema(schema) ? schema.items : schema;
};

export const isEnumSupportedType = (
  type: DataExtractionPropertyType | DataExtractionArrayItemType | null | undefined
): type is DataExtractionEnumType => {
  return (
    typeof type === 'string' && DATA_EXTRACTION_ENUM_TYPES.includes(type as DataExtractionEnumType)
  );
};

export const createEmptyDataExtractionProperty = (
  overrides?: Partial<DataExtractionPropertyFormValue>
): DataExtractionPropertyFormValue => ({
  id: createPropertyId(),
  identifier: '',
  description: '',
  dataType: 'string',
  required: false,
  arrayItemType: DEFAULT_ARRAY_ITEM_TYPE,
  enumValues: [],
  ...overrides,
});

export const stringifyDataExtractionJsonSchema = (schema?: JsonSchema | null) => {
  if (!schema) {
    return '';
  }

  return JSON.stringify(schema, null, 2);
};

export const parseDataExtractionJsonSchema = (value?: string | null) => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return {
      schema: undefined,
      error: undefined,
    };
  }

  try {
    const parsedValue = JSON.parse(trimmedValue);

    if (!isJsonSchemaObject(parsedValue)) {
      return {
        schema: undefined,
        error: 'JSON schema must be a JSON object',
      };
    }

    return {
      schema: parsedValue,
      error: undefined,
    };
  } catch {
    return {
      schema: undefined,
      error: 'Enter valid JSON schema',
    };
  }
};

export const isDataExtractionBuilderCompatible = (schema?: JsonSchema | null) => {
  if (!schema) {
    return true;
  }

  return (
    isDataExtractionObjectRootBuilderCompatible(schema) || isDataExtractionRootArraySchema(schema)
  );
};

const buildScalarSchema = (
  type: DataExtractionPropertyType | DataExtractionArrayItemType,
  enumValues: string[] = []
): JsonSchema => {
  const schema: JsonSchema = {
    type,
  };

  if (isEnumSupportedType(type)) {
    const normalizedEnumValues = normalizeEnumValues(enumValues);

    if (normalizedEnumValues.length > 0) {
      schema.enum = normalizedEnumValues.map(value => parseEnumValue(value, type));
    }
  }

  return schema;
};

const buildPropertySchema = (property: DataExtractionPropertyFormValue): JsonSchema => {
  const description = property.description.trim();
  const descriptionField = description ? { description } : {};

  if (property.dataType === 'array') {
    const itemType = property.arrayItemType ?? DEFAULT_ARRAY_ITEM_TYPE;

    return {
      type: 'array',
      items: buildScalarSchema(itemType, property.enumValues),
      ...descriptionField,
    };
  }

  if (property.dataType === 'object') {
    return {
      type: 'object',
      additionalProperties: true,
      ...descriptionField,
    };
  }

  return {
    ...buildScalarSchema(property.dataType, property.enumValues),
    ...descriptionField,
  };
};

export const buildDataExtractionJsonSchema = (
  properties: Array<Partial<DataExtractionPropertyFormValue>> = [],
  options?: BuildDataExtractionJsonSchemaOptions
): JsonSchema => {
  const propertiesMap: Record<string, JsonSchema> = {};
  const required: string[] = [];

  properties.forEach(rawProperty => {
    const property = createEmptyDataExtractionProperty(rawProperty);
    const identifier = property.identifier.trim();

    if (!identifier) {
      return;
    }

    propertiesMap[identifier] = buildPropertySchema({
      ...property,
      identifier,
    });

    if (property.required) {
      required.push(identifier);
    }
  });

  const objectSchema: JsonSchema = {
    type: 'object',
    additionalProperties: false,
    properties: propertiesMap,
    ...(required.length > 0 ? { required } : {}),
  };

  if (options?.rootIsArray) {
    return {
      type: 'array',
      items: objectSchema,
    };
  }

  return objectSchema;
};

export const mapJsonSchemaToDataExtractionProperties = (
  schema?: JsonSchema | null
): DataExtractionPropertyFormValue[] => {
  const objectSchema = getDataExtractionObjectRootSchema(schema);

  if (!objectSchema?.properties) {
    return [];
  }

  const required = new Set(objectSchema.required ?? []);

  return Object.entries(objectSchema.properties).map(([identifier, propertySchema]) => {
    const dataType = pickSchemaType(propertySchema);
    const enumSource = dataType === 'array' ? propertySchema.items : propertySchema;
    const arrayItemType =
      dataType === 'array'
        ? (pickSchemaType(propertySchema.items) as DataExtractionArrayItemType)
        : null;

    return createEmptyDataExtractionProperty({
      identifier,
      description: propertySchema.description ?? '',
      dataType,
      required: required.has(identifier),
      arrayItemType,
      enumValues: enumSource?.enum?.map(stringifyEnumValue) ?? [],
    });
  });
};

const normalizeTemplateType = (value?: string | null) => {
  if (!value) {
    return undefined;
  }

  return PROPERTY_TYPE_ALIASES[value.trim().toLowerCase()];
};

const isJsonTemplateDocument = (value: unknown): value is JsonTemplateDocument => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'properties' in value &&
    Array.isArray((value as JsonTemplateDocument).properties)
  );
};

const mapTemplateDocumentToProperties = (document: JsonTemplateDocument) => {
  return (document.properties ?? []).map(property => {
    const dataType = normalizeTemplateType(property.dataType) ?? 'string';
    const normalizedArrayItemType = normalizeTemplateType(property.arrayItemType ?? undefined);

    return createEmptyDataExtractionProperty({
      identifier: property.identifier?.trim() ?? '',
      description: property.description?.trim() ?? '',
      dataType,
      required: Boolean(property.required),
      arrayItemType:
        dataType === 'array'
          ? normalizedArrayItemType && normalizedArrayItemType !== 'array'
            ? normalizedArrayItemType
            : 'string'
          : null,
      enumValues: Array.isArray(property.enumValues)
        ? property.enumValues.map(value => String(value).trim()).filter(Boolean)
        : [],
    });
  });
};

export const parseJsonEditorValue = (value: string): JsonEditorParseResult => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return {
      error: undefined,
      isTemplate: false,
      schema: undefined,
      properties: [],
    };
  }

  try {
    const parsedValue = JSON.parse(trimmedValue);

    if (isJsonTemplateDocument(parsedValue)) {
      const properties = mapTemplateDocumentToProperties(parsedValue);

      return {
        error: undefined,
        isTemplate: true,
        schema: buildDataExtractionJsonSchema(properties),
        properties,
      };
    }
  } catch {
    return {
      error: 'Enter valid JSON',
      isTemplate: false,
      schema: undefined,
      properties: [],
    };
  }

  const parsedSchema = parseDataExtractionJsonSchema(trimmedValue);

  return {
    error: parsedSchema.error,
    isTemplate: false,
    schema: parsedSchema.schema,
    properties:
      parsedSchema.schema && isDataExtractionBuilderCompatible(parsedSchema.schema)
        ? mapJsonSchemaToDataExtractionProperties(parsedSchema.schema)
        : [],
  };
};
